import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { AdvertCarousel, AdvertPanel } from "../components/adverts";
import { AnalyticsPage } from "./analytics";
import { InstallBanner } from "./auth";
import { ContactsPage } from "./contacts";
import { API, CashbackBadge, DiscountBadge, Icon, NAV_ITEMS, PROVIDERS, StatCard, TopItMark, api, computeCashbackRate, serviceDiscountPct, naira, paymentCallbackUrl, useAuth, enablePush, pushPermission, pushSupported, detectNetworkClient } from "../lib/core";
import { GroupsPage } from "./groups";
import { NotificationsDropdown } from "../components/notifications";
import { ProfilePage } from "./profile";
import { NotificationSettingsPage } from "./notif-settings";
import { RefundPage } from "./refunds";
import { ReferralProgram } from "./referral";
import { ScheduledRechargesPage } from "./schedules";
import {
  useGamification, HomeSummaryStrip, BecauseYouUsually, OffersCarousel,
  TrendingDeals, AchievementsRow, RewardsWheelModal, PostPurchaseCelebration,
} from "../components/gamification";
import { AIAssistant, SavingsCoachCard } from "../components/ai-assistant";
import { CompactTxRow, DataPlanSelector, ProviderCard, RechargeConfirmationModal, RechargePaymentModal, ServiceTypeButton, TransactionItem } from "../components/vend";
import { WalletPage } from "./wallet";
import { FamilyPage } from "./family";
import { DiscoverPage } from "./discover";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const CASHBACK_GIFT_IMG = "https://static.prod-images.emergentagent.com/jobs/9fdefcef-811d-41c0-8d66-ac5c9b6c2050/images/0c676b19066091d1bed24ea8968dba537d010ba52be44d2bf660865835ad40ba.png";
const SAVINGS_PIGGY_IMG = "https://static.prod-images.emergentagent.com/jobs/9fdefcef-811d-41c0-8d66-ac5c9b6c2050/images/0d7feb0d23b7503f18e8f7c9aa5879f15a543f761b632f3a0b6655c6a8578baf.png";

export const Sidebar = ({
  activeView,
  walletBalance,
  walletLoading,
  user,
  services = {},
  onNavigate,
  onOpenWallet,
  onOpenBeneficiaries,
  onOpenContacts,
  onOpenSchedules,
  onOpenSettings,
  onOpenNotifSettings,
  onLogout,
  mobileOpen,
  onMobileClose,
}) => {
  const handleClick = (item) => {
    if (item.kind === "modal") {
      if (item.id === "wallet") onOpenWallet();
      else if (item.id === "beneficiaries") onOpenBeneficiaries();
      else if (item.id === "contacts") onOpenContacts();
      else if (item.id === "schedules") onOpenSchedules();
      else if (item.id === "settings") onOpenSettings();
      else if (item.id === "notifications_settings") onOpenNotifSettings();
    } else {
      onNavigate(item.id);
    }
    if (mobileOpen) onMobileClose();
  };

  const content = (
    <div className="flex flex-col h-full bg-sidebar-dark text-white w-64">
      {/* Logo */}
      <div className="px-6 pb-6 flex items-center justify-between" style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}>
        <TopItMark size="md" variant="light" />
        <button
          onClick={onMobileClose}
          className="lg:hidden text-slate-400 hover:text-white"
          data-testid="sidebar-close-mobile"
        >
          <Icon name="close" />
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
        {NAV_ITEMS.filter((item) => (item.id !== "cable_tv" && item.id !== "electricity") || services[item.id]).map((item) => {
          const isActive = item.kind === "view" && activeView === item.id;
          return (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => handleClick(item)}
              className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium transition-all group ${
                isActive
                  ? "bg-primary-custom text-white shadow-lg shadow-primary-custom/40"
                  : "text-slate-300 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon name={item.icon} className={isActive ? "text-white" : "text-slate-400 group-hover:text-white"} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.soon && (
                <span className="text-[10px] uppercase tracking-wider bg-yellow-500/20 text-yellow-300 px-1.5 py-0.5 rounded">Soon</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Wallet quick-access at bottom */}
      <div className="border-t border-white/10 p-4">
        <p className="text-slate-400 text-xs font-medium">Wallet Balance</p>
        {walletLoading ? (
          <div className="mt-1.5 h-6 w-28 rounded-md bg-white/10 animate-pulse" data-testid="sidebar-wallet-skeleton" />
        ) : (
          <p className="text-white text-xl font-bold mt-1" data-testid="sidebar-wallet-balance">
            ₦{Number(walletBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        )}
        <button
          data-testid="sidebar-fund-wallet"
          onClick={onOpenWallet}
          className="mt-3 w-full h-10 bg-primary-custom hover:bg-primary-dark text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
        >
          <Icon name="add" className="text-base" />
          <span>Fund Wallet</span>
        </button>

        {/* User mini-profile */}
        <button
          onClick={onOpenSettings}
          className="mt-4 w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-all"
          data-testid="sidebar-profile"
        >
          {user?.profile_picture ? (
            <img src={user.profile_picture} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <Icon name="person" className="text-slate-300" />
            </div>
          )}
          <div className="flex-1 text-left min-w-0">
            <p className="text-white text-sm font-semibold truncate">{user?.full_name || "User"}</p>
            <p className="text-slate-400 text-xs truncate">{user?.email}</p>
          </div>
          <Icon name="chevron_right" className="text-slate-400 text-base" />
        </button>
        <button
          onClick={onLogout}
          data-testid="sidebar-logout"
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all"
        >
          <Icon name="logout" className="text-base" />
          <span>Log out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar (fixed) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-40" data-testid="sidebar-desktop">
        {content}
      </aside>
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onMobileClose} />
          <div className="relative animate-fadeIn" data-testid="sidebar-mobile">{content}</div>
        </div>
      )}
    </>
  );
};

// Top header bar (page title + bell + user)

export const TopHeader = ({ title, subtitle, unreadNotifications, onOpenNotifications, onOpenProfile, onLogout, onOpenMobileNav, user }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 bg-background-light/80 backdrop-blur-md border-b border-slate-200/70">
      <div className="px-4 lg:px-8 pb-4 flex items-center gap-4" style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}>
        <button
          onClick={onOpenMobileNav}
          className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
          data-testid="open-mobile-nav"
        >
          <Icon name="menu" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-slate-900 text-xl lg:text-2xl font-bold tracking-tight truncate">{title}</h1>
          {subtitle && <p className="text-slate-500 text-sm mt-0.5 truncate">{subtitle}</p>}
        </div>

        <button
          data-testid="notifications-btn"
          onClick={onOpenNotifications}
          className="relative p-2 rounded-xl hover:bg-slate-100 text-slate-600"
        >
          <Icon name="notifications" className="text-xl" />
          {unreadNotifications > 0 && (
            <span className="absolute top-1 right-1 w-5 h-5 bg-primary-custom text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-background-light">
              {unreadNotifications > 9 ? "9+" : unreadNotifications}
            </span>
          )}
        </button>

        <div className="relative">
          <button
            data-testid="profile-btn"
            onClick={() => setMenuOpen((s) => !s)}
            className="flex items-center gap-2 p-1.5 pr-3 rounded-full hover:bg-slate-100 transition-all"
          >
            {user?.profile_picture ? (
              <img src={user.profile_picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                <Icon name="person" className="text-slate-500 text-lg" />
              </div>
            )}
            <span className="hidden md:inline text-sm font-semibold text-slate-700 truncate max-w-[120px]">{user?.full_name?.split(" ")[0] || "User"}</span>
            <Icon name="expand_more" className="text-slate-400 text-base" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-20" data-testid="profile-menu">
                <button onClick={() => { setMenuOpen(false); onOpenProfile(); }} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="menu-settings">
                  <Icon name="settings" className="text-base text-slate-400" />Settings
                </button>
                <div className="border-t border-slate-100 my-1" />
                <button onClick={() => { setMenuOpen(false); onLogout(); }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid="menu-logout">
                  <Icon name="logout" className="text-base" />Log out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

// Small stat card

export const ComingSoonView = ({ label, icon }) => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="text-center max-w-md px-4" data-testid={`coming-soon-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-primary-custom to-primary-dark flex items-center justify-center shadow-lg shadow-primary-custom/30">
        <Icon name={icon} className="text-white text-4xl" />
      </div>
      <h2 className="mt-6 text-2xl font-bold text-slate-900">{label} — Coming Soon</h2>
      <p className="mt-2 text-slate-500">
        We're cooking up {label.toLowerCase()} payments for you. Stay tuned — you'll be the first to know when it's live.
      </p>
      <span className="inline-block mt-6 px-4 py-2 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-full text-xs font-semibold tracking-wide">
        IN DEVELOPMENT
      </span>
    </div>
  </div>
);

// Support view

export const SupportView = () => (
  <div className="max-w-3xl mx-auto" data-testid="support-view">
    <div className="bg-gradient-to-br from-primary-custom to-primary-dark rounded-3xl p-8 text-white shadow-xl shadow-primary-custom/20 mb-6 overflow-hidden relative">
      <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
      <div className="relative">
        <Icon name="headset_mic" className="text-5xl" />
        <h2 className="mt-3 text-2xl font-bold">We're here to help.</h2>
        <p className="text-white/80 mt-1.5 max-w-md">Reach out anytime — our team typically responds within an hour during business hours.</p>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <a href="mailto:support@top-it.app" className="bg-white p-5 rounded-2xl border border-slate-200 hover:shadow-md transition-all group" data-testid="support-email">
        <div className="w-10 h-10 bg-primary-custom/10 rounded-lg flex items-center justify-center group-hover:bg-primary-custom/20 transition-colors">
          <Icon name="mail" className="text-primary-custom" />
        </div>
        <h3 className="mt-3 font-bold text-slate-900">Email Support</h3>
        <p className="text-sm text-slate-500 mt-1">support@top-it.app</p>
      </a>
      <a href="https://wa.me/2348000000000" target="_blank" rel="noreferrer" className="bg-white p-5 rounded-2xl border border-slate-200 hover:shadow-md transition-all group" data-testid="support-whatsapp">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
          <Icon name="chat" className="text-green-600" />
        </div>
        <h3 className="mt-3 font-bold text-slate-900">WhatsApp Chat</h3>
        <p className="text-sm text-slate-500 mt-1">Quick replies, 24/7</p>
      </a>
    </div>
  </div>
);

// Vend form — reusable for airtime and data flows

const PartnerCodeApply = () => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(() => { try { return localStorage.getItem("topit_partner_code") || ""; } catch { return ""; } });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("/partners/apply", { code: code.trim().toUpperCase() });
      if (r.data.applied) {
        setMsg({ type: "success", text: `Partner code ${r.data.partner_code} applied!` });
        try { localStorage.removeItem("topit_partner_code"); } catch { /* noop */ }
      } else if (r.data.reason === "already_attributed") {
        setMsg({ type: "info", text: `You're already linked to partner ${r.data.partner_code}.` });
      } else {
        setMsg({ type: "error", text: "Invalid partner code." });
      }
    } catch {
      setMsg({ type: "error", text: "Could not apply code. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const toneClass = msg?.type === "success" ? "text-green-700" : msg?.type === "error" ? "text-red-600" : "text-slate-500";

  return (
    <div data-testid="checkout-partner-section">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="checkout-partner-toggle" className="text-sm text-slate-500 hover:text-primary-custom flex items-center gap-1.5">
          <Icon name="handshake" className="text-base" /> Have a partner code?
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <input
            data-testid="checkout-partner-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Enter partner code"
            className="flex-1 h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-primary-custom uppercase"
          />
          <button type="button" onClick={apply} disabled={busy || !code.trim()} data-testid="checkout-partner-apply" className="h-11 px-4 bg-slate-900 text-white rounded-xl font-semibold disabled:opacity-50">
            {busy ? "..." : "Apply"}
          </button>
        </div>
      )}
      {msg && <p data-testid="checkout-partner-msg" className={`text-sm mt-1.5 ${toneClass}`}>{msg.text}</p>}
    </div>
  );
};


export const VendForm = ({ initialServiceType = "airtime", dataPlans, dataPlansLoading, dataPlansError, refetchDataPlans, walletBalance, onTransactionComplete }) => {
  const [provider, setProvider] = useState("");
  const [serviceType, setServiceType] = useState(initialServiceType);
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [useWallet, setUseWallet] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [flwPayment, setFlwPayment] = useState(null); // { tx_ref, payment_link, amount }
  const [confirm, setConfirm] = useState(null); // recharge confirmation pop
  const [rates, setRates] = useState(null);

  useEffect(() => { setServiceType(initialServiceType); setSelectedPlan(null); setAmount(""); }, [initialServiceType]);

  useEffect(() => {
    api.get("/rewards/rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  if (showContactPicker) {
    return (
      <ContactsPage
        pickMode
        onBack={() => setShowContactPicker(false)}
        onPick={({ phone: p, provider: prov }) => {
          setPhone(p);
          if (prov) setProvider(prov);
          setShowContactPicker(false);
        }}
      />
    );
  }

  const handleProviderSelect = (p) => { setProvider(p); setSelectedPlan(null); };
  const handlePlanSelect = (plan) => { setSelectedPlan(plan); setAmount(plan.amount.toString()); };

  const handleTopup = async (e) => {
    e.preventDefault();
    if (!provider || !phone || !amount) return;
    setLoading(true); setResult(null);
    try {
      if (useWallet) {
        const payload = {
          customer_phone: phone,
          amount: parseFloat(amount),
          provider,
          service_type: serviceType,
          use_wallet: true,
        };
        if (serviceType === "data" && selectedPlan) payload.subcode = selectedPlan.subCode;
        const response = await api.post("/vend/wallet", payload);
        setResult(response.data);
        if (onTransactionComplete) onTransactionComplete(true);
      } else {
        // Pay-for-service via Flutterwave (card / bank / USSD)
        const payload = {
          customer_phone: phone,
          amount: parseFloat(amount),
          provider,
          service_type: serviceType,
          redirect_url: paymentCallbackUrl(),
        };
        if (serviceType === "data" && selectedPlan) payload.subcode = selectedPlan.subCode;
        const res = await api.post("/payments/flutterwave/recharge/initiate", payload);
        if (res.data.success) {
          setFlwPayment({ ...res.data, provider, customer_phone: phone });
        }
      }
    } catch (err) {
      setResult({ status_code: "error", response_message: err.response?.data?.detail || "Transaction failed" });
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [100, 200, 500, 1000, 2000, 5000];
  const providerPlans = dataPlans[provider] || [];

  return (
    <form onSubmit={handleTopup} className="space-y-5 max-w-3xl">
      {/* Provider selection */}
      <div className="bg-white rounded-2xl p-5 lg:p-6 space-y-4 border border-slate-200/70">
        <h2 className="font-bold text-slate-900 flex items-center gap-2 text-sm uppercase tracking-wide">
          <Icon name="sim_card" className="text-primary-custom" /> Select Network
        </h2>
        <div className="grid grid-cols-4 gap-3">
          {PROVIDERS.map((p) => (
            <ProviderCard key={p.name} provider={p} selected={provider === p.name} onSelect={handleProviderSelect} />
          ))}
        </div>
      </div>

      {/* Service type (only shown when not constrained) */}
      {initialServiceType !== "data" && initialServiceType !== "airtime" && (
        <div className="bg-white rounded-2xl p-5 lg:p-6 space-y-4 border border-slate-200/70">
          <h2 className="font-bold text-slate-900 flex items-center gap-2 text-sm uppercase tracking-wide">
            <Icon name="category" className="text-primary-custom" /> Select Service
          </h2>
          <div className="flex gap-3">
            <ServiceTypeButton type="airtime" icon="phone_in_talk" label="Airtime" selected={serviceType === "airtime"} onClick={() => { setServiceType("airtime"); setSelectedPlan(null); setAmount(""); }} />
            <ServiceTypeButton type="data" icon="wifi" label="Data" selected={serviceType === "data"} onClick={() => { setServiceType("data"); setSelectedPlan(null); setAmount(""); }} />
          </div>
        </div>
      )}

      {/* Data plans */}
      {serviceType === "data" && provider && (
        <DataPlanSelector
          plans={providerPlans}
          loading={dataPlansLoading}
          error={dataPlansError}
          onRetry={refetchDataPlans}
          selectedPlan={selectedPlan}
          onSelect={handlePlanSelect}
          cashbackRate={computeCashbackRate(rates, "data", provider)}
          discountPct={serviceDiscountPct(rates, "data")}
        />
      )}

      {/* Phone */}
      <div className="bg-white rounded-2xl p-5 lg:p-6 space-y-4 border border-slate-200/70">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-slate-900 flex items-center gap-2 text-sm uppercase tracking-wide">
            <Icon name="call" className="text-primary-custom" /> Phone Number
          </h2>
          <button type="button" onClick={() => setShowContactPicker(true)} className="flex items-center gap-1.5 text-primary-custom font-semibold text-sm hover:bg-primary-custom/5 px-2.5 py-1.5 rounded-lg transition-colors" data-testid="open-contact-picker">
            <Icon name="contacts" className="text-lg" /> Contacts
          </button>
        </div>
        <div className="relative">
          <Icon name="phone_android" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="phone-input"
            type="tel"
            value={phone}
            onChange={(e) => {
              const v = e.target.value;
              setPhone(v);
              const net = detectNetworkClient(v);
              if (net && net !== provider) { setProvider(net); setSelectedPlan(null); }
            }}
            placeholder="Enter phone number"
            className="w-full h-14 pl-12 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 outline-none transition-all text-lg font-medium"
            required
          />
          {phone && detectNetworkClient(phone) && (
            <p className="text-xs text-slate-500 mt-1.5 pl-1" data-testid="detected-network">Network: <span className="font-semibold">{detectNetworkClient(phone)}</span> (auto-selected from the number)</p>
          )}
        </div>
      </div>

      {/* Amount for airtime */}
      {serviceType === "airtime" && (
        <div className="bg-white rounded-2xl p-5 lg:p-6 space-y-4 border border-slate-200/70">
          <h2 className="font-bold text-slate-900 flex items-center gap-2 text-sm uppercase tracking-wide">
            <Icon name="payments" className="text-primary-custom" /> Amount
          </h2>
          <input
            data-testid="amount-input"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter amount"
            className="w-full h-14 px-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 outline-none transition-all text-2xl font-bold text-center"
            required
          />
          <div className="flex flex-wrap gap-2">
            {quickAmounts.map((amt) => (
              <button
                key={amt}
                type="button"
                data-testid={`quick-amount-${amt}`}
                onClick={() => setAmount(amt.toString())}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${amount === amt.toString() ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                ₦{amt.toLocaleString()}
              </button>
            ))}
          </div>
          {serviceDiscountPct(rates, "airtime") > 0 && parseFloat(amount || 0) > 0 && (
            <div className="flex items-center justify-between rounded-xl bg-primary-custom/5 border border-primary-custom/20 px-4 py-2.5" data-testid="airtime-savings">
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-custom">
                <Icon name="sell" className="text-base" /> {serviceDiscountPct(rates, "airtime")}% off — you pay ₦{Math.round(parseFloat(amount) * (1 - serviceDiscountPct(rates, "airtime") / 100)).toLocaleString()}
              </span>
              <span className="text-xs text-slate-400 line-through">₦{parseFloat(amount).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      {/* Plan summary */}
      {serviceType === "data" && selectedPlan && (
        <div className="bg-primary-custom/5 border border-primary-custom/20 rounded-2xl p-5 lg:p-6 flex justify-between items-center">
          <div>
            <p className="font-bold text-primary-custom text-lg">{selectedPlan.subCode}</p>
            <p className="text-sm text-slate-600">{selectedPlan.description}</p>
          </div>
          <p className="text-2xl font-bold text-primary-custom">₦{selectedPlan.amount.toLocaleString()}</p>
        </div>
      )}

      {/* Payment method */}
      <div className="bg-white rounded-2xl p-5 lg:p-6 space-y-4 border border-slate-200/70">
        <h2 className="font-bold text-slate-900 flex items-center gap-2 text-sm uppercase tracking-wide">
          <Icon name="payments" className="text-primary-custom" /> Payment Method
        </h2>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="pay-direct"
            onClick={() => setUseWallet(false)}
            className={`flex-1 py-4 px-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${!useWallet ? "border-primary-custom bg-primary-custom/5" : "border-slate-200 hover:border-slate-300"}`}
          >
            <div className="flex items-center gap-2">
              <Icon name="credit_card" className={!useWallet ? "text-primary-custom" : "text-slate-400"} />
              <span className={`font-medium ${!useWallet ? "text-primary-custom" : "text-slate-600"}`}>Card / Bank</span>
            </div>
            <span className={`text-xs ${!useWallet ? "text-primary-custom/70" : "text-slate-400"}`}>Pay via Flutterwave</span>
          </button>
          <button
            type="button"
            data-testid="pay-wallet"
            onClick={() => setUseWallet(true)}
            className={`flex-1 py-4 px-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${useWallet ? "border-primary-custom bg-primary-custom/5" : "border-slate-200 hover:border-slate-300"}`}
          >
            <div className="flex items-center gap-2">
              <Icon name="account_balance_wallet" className={useWallet ? "text-primary-custom" : "text-slate-400"} />
              <span className={`font-medium ${useWallet ? "text-primary-custom" : "text-slate-600"}`}>Wallet</span>
            </div>
            <span className={`text-xs ${useWallet ? "text-primary-custom/70" : "text-slate-400"}`}>₦{Number(walletBalance).toLocaleString()} available</span>
          </button>
        </div>
        {useWallet && parseFloat(amount || 0) > walletBalance && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 text-sm">
            <Icon name="error" /> <span>Insufficient wallet balance</span>
          </div>
        )}
        {useWallet && serviceType === "data" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 text-emerald-700 text-sm">
            <Icon name="redeem" /> <span>Get 5% cashback on this data purchase!</span>
          </div>
        )}
        {useWallet && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2 text-green-700 text-sm">
            <Icon name="trending_up" /> <span>Earn 2% cashback on this recharge!</span>
          </div>
        )}
        {!useWallet && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-2 text-green-700 text-sm">
            <Icon name="verified" className="mt-0.5" /> <span>Secure payment with Card, Bank Transfer or USSD via Flutterwave. Your recharge is delivered after payment is confirmed.</span>
          </div>
        )}
      </div>

      {/* Partner / referral code at checkout */}
      <PartnerCodeApply />

      {/* Submit */}
      <button
        data-testid="topup-submit"
        type="submit"
        disabled={loading || !provider || !phone || !amount || (serviceType === "data" && !selectedPlan) || (useWallet && parseFloat(amount || 0) > walletBalance)}
        className="w-full h-14 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-custom/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <><Icon name="progress_activity" className="animate-spin" /><span>Processing...</span></>
        ) : (
          <span className="flex items-center gap-2 flex-wrap justify-center">
            <Icon name="send" /><span>{useWallet ? "Pay with Wallet" : `Pay ₦${amount ? Number(amount).toLocaleString() : "0"} with Card`}</span>
            <CashbackBadge rates={rates} serviceType={serviceType} provider={provider} className="bg-white/20 px-2 py-0.5 rounded-full" />
            <DiscountBadge rates={rates} serviceType={serviceType} className="bg-white/20 px-2 py-0.5 rounded-full" />
          </span>
        )}
      </button>

      {/* Flutterwave recharge payment pending modal */}
      {flwPayment && (
        <RechargePaymentModal
          payment={flwPayment}
          serviceType={serviceType}
          onClose={() => setFlwPayment(null)}
          onVerified={(vend, recharge) => {
            setFlwPayment(null);
            const delivered = vend.status_code === "000" || vend.status_code === "OK";
            if (recharge) {
              setConfirm({ ...recharge, delivered });
            } else {
              setResult(vend);
            }
            if (onTransactionComplete) onTransactionComplete(false, !!recharge);
          }}
        />
      )}

      {confirm && <RechargeConfirmationModal data={confirm} onClose={() => setConfirm(null)} />}

      {/* Result */}
      {result && (
        <div
          data-testid="topup-result"
          className={`p-6 rounded-2xl ${
            result.status_code === "000" || result.status_code === "OK"
              ? "bg-green-50 border border-green-200"
              : result.status_code === "PENDING"
              ? "bg-yellow-50 border border-yellow-200"
              : "bg-red-50 border border-red-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <Icon
              name={result.status_code === "000" || result.status_code === "OK" ? "check_circle" : result.status_code === "PENDING" ? "schedule" : "error"}
              className={`text-3xl ${result.status_code === "000" || result.status_code === "OK" ? "text-green-600" : result.status_code === "PENDING" ? "text-yellow-600" : "text-red-600"}`}
            />
            <div>
              <p className="font-bold text-slate-900">
                {result.status_code === "000" || result.status_code === "OK" ? "Success!" : result.status_code === "PENDING" ? "Processing..." : "Failed"}
              </p>
              <p className="text-sm text-slate-600">{result.response_message}</p>
            </div>
          </div>
          {result.status_code === "PENDING" && (
            <p className="text-xs text-yellow-700 mt-3 bg-yellow-100 p-2 rounded-lg">
              The recharge is being processed. You'll receive confirmation once complete.
            </p>
          )}
        </div>
      )}
    </form>
  );
};

// Quick Top-Up panel on Dashboard home

export const QuickTopUpPanel = ({ walletBalance, dataPlans = {}, dataPlansLoading = false, onComplete }) => {
  const [tab, setTab] = useState("airtime"); // airtime | data
  const [provider, setProvider] = useState("MTN");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("200");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [flwPayment, setFlwPayment] = useState(null);
  const [confirm, setConfirm] = useState(null); // recharge confirmation pop
  const [rates, setRates] = useState(null);

  useEffect(() => {
    api.get("/rewards/rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  const presets = [100, 200, 500, 1000];
  const providerPlans = dataPlans[provider] || [];

  const handleProvider = (name) => { setProvider(name); setSelectedPlan(null); };
  const handleTab = (t) => { setTab(t); setMsg(null); setSelectedPlan(null); };

  const applyVendResult = (data, viaWallet) => {
    const code = data.status_code;
    const pending = code === "PENDING";
    const ok = code === "000" || code === "OK";
    setMsg({ status: pending ? "pending" : ok ? "ok" : "failed", text: data.response_message });
    if ((ok || pending) && onComplete) onComplete(viaWallet);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!phone) return;
    let amt;
    if (tab === "data") {
      if (!selectedPlan) { setMsg({ status: "failed", text: "Select a data plan." }); return; }
      amt = selectedPlan.amount;
    } else {
      if (!amount) return;
      amt = parseFloat(amount);
    }
    if (!amt || amt <= 0) { setMsg({ status: "failed", text: "Enter a valid amount." }); return; }
    setLoading(true); setMsg(null);
    try {
      const useWalletPay = walletBalance >= amt;
      if (useWalletPay) {
        const payload = { customer_phone: phone, amount: amt, provider, service_type: tab, use_wallet: true };
        if (tab === "data" && selectedPlan) payload.subcode = selectedPlan.subCode;
        const res = await api.post("/vend/wallet", payload);
        applyVendResult(res.data, true);
      } else {
        // Insufficient wallet → pay via Flutterwave (card / bank / USSD)
        const payload = { customer_phone: phone, amount: amt, provider, service_type: tab, redirect_url: paymentCallbackUrl() };
        if (tab === "data" && selectedPlan) payload.subcode = selectedPlan.subCode;
        const res = await api.post("/payments/flutterwave/recharge/initiate", payload);
        if (res.data.success) { setFlwPayment({ ...res.data, provider, customer_phone: phone }); }
      }
    } catch (err) {
      setMsg({ status: "failed", text: err.response?.data?.detail || "Transaction failed" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200/70 shadow-sm" data-testid="quick-topup-panel">
      <h3 className="font-bold text-slate-900 text-lg">Quick Top-Up</h3>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-slate-200 mt-4">
        {["airtime", "data"].map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`quick-tab-${t}`}
            onClick={() => handleTab(t)}
            className={`pb-2 text-sm font-semibold capitalize transition-colors relative ${tab === t ? "text-primary-custom" : "text-slate-500 hover:text-slate-700"}`}
          >
            {t === "airtime" ? "Airtime" : "Data"}
            {tab === t && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary-custom rounded-full" />}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-5 space-y-4">
        {/* Provider selector with network logos */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {PROVIDERS.map((p) => {
            const selected = provider === p.name;
            return (
              <button
                type="button"
                key={p.name}
                onClick={() => handleProvider(p.name)}
                data-testid={`quick-provider-${p.name.toLowerCase()}`}
                className={`flex-shrink-0 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border-2 transition-all ${selected ? "border-primary-custom bg-primary-custom/5" : "border-slate-200 hover:border-slate-300"}`}
              >
                <span className="w-7 h-7 rounded-full overflow-hidden border border-slate-200 bg-white shrink-0">
                  <img src={p.logo} alt={p.name} className="w-full h-full object-cover" />
                </span>
                <span className={`text-xs font-semibold ${selected ? "text-primary-custom" : "text-slate-600"}`}>{p.name}</span>
              </button>
            );
          })}
        </div>

        {/* Phone */}
        <div className="relative">
          <Icon name="person" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base" />
          <input
            type="tel"
            value={phone}
            onChange={(e) => {
              const v = e.target.value;
              setPhone(v);
              const net = detectNetworkClient(v);
              if (net && net !== provider) { setProvider(net); setSelectedPlan(null); }
            }}
            placeholder="Enter phone number"
            className="w-full h-11 pl-10 pr-24 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 outline-none transition-all text-sm"
            data-testid="quick-phone"
            required
          />
          <button
            type="button"
            onClick={() => setShowContactPicker(true)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1 text-primary-custom font-semibold text-xs bg-primary-custom/10 hover:bg-primary-custom/20 px-2.5 py-1.5 rounded-lg transition-colors"
            data-testid="quick-open-contacts"
          >
            <Icon name="contacts" className="text-base" /> Contacts
          </button>
        </div>

        {tab === "airtime" ? (
          /* Preset amounts */
          <div className="grid grid-cols-4 gap-2">
            {presets.map((amt) => (
              <button
                type="button"
                key={amt}
                data-testid={`quick-preset-${amt}`}
                onClick={() => setAmount(amt.toString())}
                className={`py-2 rounded-lg border text-sm font-semibold transition-all ${amount === amt.toString() ? "border-primary-custom bg-primary-custom/5 text-primary-custom" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
              >
                ₦{amt}
              </button>
            ))}
            {serviceDiscountPct(rates, "airtime") > 0 && parseFloat(amount || 0) > 0 && (
              <div className="col-span-4 flex items-center justify-between rounded-lg bg-primary-custom/5 border border-primary-custom/20 px-3 py-2 mt-1" data-testid="quick-airtime-savings">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-custom">
                  <Icon name="sell" className="text-sm" /> {serviceDiscountPct(rates, "airtime")}% off — pay ₦{Math.round(parseFloat(amount) * (1 - serviceDiscountPct(rates, "airtime") / 100)).toLocaleString()}
                </span>
                <span className="text-[11px] text-slate-400 line-through">₦{parseFloat(amount).toLocaleString()}</span>
              </div>
            )}
          </div>
        ) : (
          /* Data plan selector — grid with search / filters / sort (matches Data Bundles) */
          <DataPlanSelector
            plans={providerPlans}
            loading={dataPlansLoading}
            selectedPlan={selectedPlan}
            onSelect={setSelectedPlan}
            maxHeight="380px"
            cashbackRate={computeCashbackRate(rates, "data", provider)}
          discountPct={serviceDiscountPct(rates, "data")}
          />
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="quick-topup-submit"
          className="w-full h-11 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? (<><Icon name="progress_activity" className="animate-spin text-base" /><span>Processing...</span></>) : (
            <span className="flex items-center gap-2 flex-wrap justify-center">
              <span>Top Up Now</span>
              <CashbackBadge rates={rates} serviceType={tab} provider={provider} className="bg-white/20 px-2 py-0.5 rounded-full" />
              <DiscountBadge rates={rates} serviceType={tab} className="bg-white/20 px-2 py-0.5 rounded-full" />
            </span>
          )}
        </button>

        {msg && (
          <div
            className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${
              msg.status === "ok"
                ? "bg-green-50 text-green-700"
                : msg.status === "pending"
                ? "bg-yellow-50 text-yellow-700"
                : "bg-red-50 text-red-600"
            }`}
            data-testid="quick-topup-msg"
          >
            <Icon
              name={msg.status === "ok" ? "check_circle" : msg.status === "pending" ? "schedule" : "error"}
              className="text-base"
            />
            <span>{msg.status === "pending" ? `Processing… ${msg.text}` : msg.text}</span>
          </div>
        )}
      </form>

      {showContactPicker && (
        <div className="fixed inset-0 z-50 bg-background-light overflow-auto" data-testid="quick-contact-picker-overlay">
          <ContactsPage
            pickMode
            onBack={() => setShowContactPicker(false)}
            onPick={({ phone: p, provider: prov }) => {
              setPhone(p);
              if (prov) handleProvider(prov);
              setShowContactPicker(false);
            }}
          />
        </div>
      )}

      {flwPayment && (
        <RechargePaymentModal
          payment={flwPayment}
          serviceType={tab}
          onClose={() => setFlwPayment(null)}
          onVerified={(vend, recharge) => {
            setFlwPayment(null);
            const delivered = vend.status_code === "000" || vend.status_code === "OK";
            if (recharge) { setConfirm({ ...recharge, delivered }); if (onComplete) onComplete(false, true); }
            else { applyVendResult(vend, false); }
          }}
        />
      )}

      {confirm && <RechargeConfirmationModal data={confirm} onClose={() => setConfirm(null)} />}
    </div>
  );
};
// Recent transactions row (compact)

const PushPrompt = () => {
  const [state, setState] = useState(() => { try { return localStorage.getItem("topit_push_dismissed") ? "hidden" : pushPermission(); } catch { return pushPermission(); } });
  const [busy, setBusy] = useState(false);
  if (!pushSupported() || ["granted", "denied", "hidden", "unsupported"].includes(state)) return null;
  const enable = async () => {
    setBusy(true);
    try { await enablePush(); toast.success("Notifications enabled on this device 🎉"); setState("granted"); }
    catch (e) { toast.error(e.message || "Couldn't enable notifications"); }
    finally { setBusy(false); }
  };
  const dismiss = () => { try { localStorage.setItem("topit_push_dismissed", "1"); } catch { /* noop */ } setState("hidden"); };
  return (
    <div data-testid="push-prompt" className="bg-slate-900 text-white rounded-2xl p-4 flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center shrink-0"><Icon name="notifications_active" className="text-red-400" /></div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Turn on notifications</p>
        <p className="text-xs text-white/60">Alerts for purchases, wallet funding, referrals & auto top-ups — even when the app is closed.</p>
      </div>
      <button onClick={enable} disabled={busy} data-testid="push-enable-btn" className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-3 py-2 rounded-xl shrink-0">{busy ? "..." : "Enable"}</button>
      <button onClick={dismiss} data-testid="push-dismiss-btn" className="text-white/50 hover:text-white p-1 shrink-0"><Icon name="close" className="text-base" /></button>
    </div>
  );
};

export const DashboardHome = ({ user, walletBalance, walletLoading, transactions, stats, statsLoading, dataPlans, dataPlansLoading, onOpenWallet, onNavigate, onComplete, summary, onOpenWheel, onOpenGroups, onRefresh }) => {
  const recent = (transactions || []).slice(0, 4);
  const [showBalance, setShowBalance] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [offers, setOffers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [reminderDismissed, setReminderDismissed] = useState(false);
  const [smartReminder, setSmartReminder] = useState(null);
  const [claimingStreak, setClaimingStreak] = useState(false);

  const claimStreak = async () => {
    setClaimingStreak(true);
    try {
      const r = await api.post("/gamification/streak/claim");
      const fire = (ratio, opts) => confetti({ ...opts, origin: { y: 0.6 }, colors: ["#f97316", "#dc2626", "#fbbf24", "#10b981"], particleCount: Math.floor(180 * ratio) });
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.35, { spread: 60 });
      fire(0.2, { spread: 100, decay: 0.91, scalar: 0.9 });
      toast.success(`🔥 ${r.data.claimed}-day streak reward — ₦${Number(r.data.reward).toLocaleString()} added to your wallet!`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't claim reward");
    } finally { setClaimingStreak(false); }
  };

  useEffect(() => {
    api.get("/wallet/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/offers").then((r) => setOffers(r.data.items || [])).catch(() => {});
    api.get("/deals").then((r) => setDeals(r.data.items || [])).catch(() => {});
    api.get("/gamification/smart-reminder").then((r) => setSmartReminder(r.data?.reminder || null)).catch(() => {});
  }, []);

  const usual = summary?.because_you_usually;
  const ach = summary?.achievements || {};
  const tier = summary?.referral_tier || {};
  const bestDeal = deals[0];
  const showReminder = !reminderDismissed && !!smartReminder;
  const PLOGO = { MTN: "#FFCC00", Airtel: "#E11D2E", Glo: "#22C55E", "9mobile": "#0F9D58" };
  const brandDot = (p) => (
    <span className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-black text-slate-900" style={{ background: PLOGO[p] || "#E2E8F0" }}>{(p || "?").slice(0, 3).toUpperCase()}</span>
  );

  const statCards = [
    { key: "savings", label: "Savings This Month", value: summary?.savings_month ?? stats?.total_savings_month ?? 0, sub: "Rewards & cashback saved", icon: "savings", bg: "bg-emerald-50", ring: "bg-emerald-100", ic: "text-emerald-600", val: "text-emerald-700", cta: "View Insights", to: "analytics" },
    { key: "cashback", label: "Cashback Earned", value: analytics?.cashback_earned_this_month ?? 0, sub: `${analytics?.cashback_rate_pct ?? 5}% cashback on data`, icon: "redeem", bg: "bg-amber-50", ring: "bg-amber-100", ic: "text-amber-600", val: "text-amber-700", cta: "View Cashback", to: "wallet" },
    { key: "referral", label: "Referral Earnings", value: summary?.referral_earnings ?? 0, sub: `${tier.count ?? 0} friends referred`, icon: "diversity_3", bg: "bg-violet-50", ring: "bg-violet-100", ic: "text-violet-600", val: "text-violet-700", cta: "View Referrals", to: "referral" },
  ];

  const quickActions = [
    { icon: "smartphone", label: "Buy Airtime", ic: "text-primary-custom", bg: "bg-red-50", onClick: () => onNavigate("airtime"), testid: "qa-airtime" },
    { icon: "wifi", label: "Buy Data", ic: "text-emerald-600", bg: "bg-emerald-50", onClick: () => onNavigate("data"), testid: "qa-data" },
    { icon: "account_balance_wallet", label: "Fund Wallet", ic: "text-amber-600", bg: "bg-amber-50", onClick: onOpenWallet, testid: "qa-fund" },
    { icon: "groups", label: "Groups", ic: "text-blue-600", bg: "bg-blue-50", onClick: onOpenGroups, testid: "qa-groups" },
    { icon: "more_horiz", label: "More", ic: "text-slate-500", bg: "bg-slate-100", onClick: () => onNavigate("discover"), testid: "qa-more" },
  ];

  const SectionHead = ({ title, onViewAll }) => (
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-bold text-slate-900">{title}</h3>
      {onViewAll && <button onClick={onViewAll} className="text-primary-custom text-xs font-semibold hover:underline">View all →</button>}
    </div>
  );

  return (
    <div className="gami-home space-y-5">
      <InstallBanner />
      <PushPrompt />

      {/* Row 1: Wallet + colored stat cards */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-primary-custom via-primary-custom to-primary-dark rounded-3xl p-5 text-white relative overflow-hidden shadow-lg shadow-primary-custom/20" data-testid="wallet-hero-card">
          <Icon name="account_balance_wallet" className="absolute -right-3 -bottom-3 text-white/10 pointer-events-none" style={{ fontSize: "6rem" }} />
          <div className="relative">
            <p className="text-white/80 text-sm font-medium">Wallet Balance</p>
            <div className="flex items-center gap-2 mt-1">
              {walletLoading ? (
                <div className="h-9 w-40 rounded-lg bg-white/20 animate-pulse" data-testid="hero-wallet-skeleton" />
              ) : (
                <>
                  <p className="text-white font-extrabold text-3xl tracking-tight font-display tnum" data-testid="hero-wallet-balance">
                    {showBalance ? `₦${Number(walletBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "₦ ••••••"}
                  </p>
                  <button onClick={() => setShowBalance(!showBalance)} data-testid="hero-toggle-balance" className="p-1 hover:bg-white/10 rounded-lg">
                    <Icon name={showBalance ? "visibility" : "visibility_off"} className="text-white/80 text-base" />
                  </button>
                </>
              )}
            </div>
            <button onClick={onOpenWallet} data-testid="hero-fund-wallet" className="mt-4 inline-flex items-center gap-1.5 bg-white text-primary-custom font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-white/90 transition-colors">
              <Icon name="add" className="text-base" />Fund Wallet
            </button>
          </div>
        </div>

        {statCards.map((c) => (
          c.key === "referral" ? (
            (() => {
              const streakDays = summary?.streak?.current ?? 0;
              const ms = summary?.streak?.milestone || {};
              const goal = ms.next_value || 7;
              const pct = Math.min(streakDays / goal, 1);
              const r = 26, circ = 2 * Math.PI * r;
              return (
                <div key={c.key} className="relative overflow-hidden rounded-3xl p-5 flex items-center justify-between gap-3 bg-gradient-to-br from-[#1a1210] to-[#0b0b0d] border border-orange-900/40 min-h-[150px]" data-testid="stat-streak">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🔥</span>
                      <p className="text-white font-extrabold text-lg leading-none">{`${streakDays} Day Streak`}</p>
                    </div>
                    <p className="text-slate-400 text-sm mt-1">
                      {ms.claimable ? `You hit ${ms.value} days! 🎉` : streakDays > 0 ? "You're on fire! 🔥" : "Start your streak today!"}
                    </p>
                    {ms.claimable ? (
                      <button onClick={claimStreak} disabled={claimingStreak} data-testid="streak-claim"
                        className="mt-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 text-sm font-extrabold px-4 py-2 rounded-xl disabled:opacity-60">
                        <Icon name="redeem" className="text-base" />{claimingStreak ? "Claiming…" : `Claim ₦${Number(ms.reward).toLocaleString()}`}</button>
                    ) : (
                      <button onClick={() => onNavigate("airtime")} data-testid="streak-keep-going"
                        className="mt-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-red-600 to-orange-500 text-white text-sm font-bold px-4 py-2 rounded-xl hover:from-red-700 hover:to-orange-600 transition-colors">
                        Keep Going</button>
                    )}
                    {!ms.claimable && ms.days_to_next != null && (
                      <p className="text-[11px] text-slate-500 mt-1.5" data-testid="streak-next-hint">{ms.days_to_next} day{ms.days_to_next === 1 ? "" : "s"} to ₦{Number(ms.next_reward).toLocaleString()} bonus</p>
                    )}
                  </div>
                  <div className="relative shrink-0 w-[68px] h-[68px]" data-testid="streak-ring">
                    <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
                      <circle cx="32" cy="32" r={r} fill="none" stroke="#3a2a20" strokeWidth="6" />
                      <circle cx="32" cy="32" r={r} fill="none" stroke="url(#streakGrad)" strokeWidth="6" strokeLinecap="round"
                        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} />
                      <defs>
                        <linearGradient id="streakGrad" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#f97316" /><stop offset="100%" stopColor="#dc2626" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-white font-extrabold text-xl tnum">{streakDays}</span>
                  </div>
                </div>
              );
            })()
          ) : c.key === "cashback" ? (
            <div key={c.key} className="relative overflow-hidden rounded-3xl flex flex-col bg-[#ACE6CE] border border-emerald-200/60 min-h-[150px]" data-testid="stat-cashback">
              <img src={CASHBACK_GIFT_IMG} alt="" aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover object-right pointer-events-none select-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#ACE6CE] via-[#ACE6CE]/85 to-transparent pointer-events-none" />
              <div className="relative z-10 p-5 pointer-events-none">
                <p className="text-emerald-900 text-sm font-semibold">{c.label}</p>
                <p className="text-2xl font-extrabold text-emerald-950 mt-0.5 tnum" data-testid="cashback-value">{naira(c.value)}</p>
                <p className="text-xs text-emerald-800/70 mt-0.5">This Month</p>
                <span className="inline-flex items-center mt-3 bg-emerald-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm" data-testid="cashback-pill">
                  +{analytics?.cashback_rate_pct ?? 5}% back
                </span>
              </div>
              <button onClick={onOpenWallet} data-testid="stat-cashback-cta"
                className="absolute inset-0 z-20" aria-label="View cashback" />
            </div>
          ) : c.key === "savings" ? (
            <div key={c.key} className="relative overflow-hidden rounded-3xl flex flex-col bg-[#ABE4CB] border border-emerald-200/60 min-h-[150px]" data-testid="stat-savings">
              <img src={SAVINGS_PIGGY_IMG} alt="" aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover object-right pointer-events-none select-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#ABE4CB] via-[#ABE4CB]/85 to-transparent pointer-events-none" />
              <div className="relative z-10 p-5 pointer-events-none">
                <p className="text-emerald-900 text-sm font-semibold">{c.label}</p>
                <p className="text-2xl font-extrabold text-emerald-950 mt-0.5 tnum" data-testid="savings-value">{naira(c.value)}</p>
                <p className="text-xs text-emerald-800/70 mt-0.5">Rewards &amp; cashback saved</p>
                <span className="inline-flex items-center gap-1 mt-3 bg-emerald-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm" data-testid="savings-pill">
                  View Insights<Icon name="arrow_forward" className="text-xs" />
                </span>
              </div>
              <button onClick={() => onNavigate("analytics")} data-testid="stat-savings-cta"
                className="absolute inset-0 z-20" aria-label="View savings insights" />
            </div>
          ) : (
            <div key={c.key} className={`${c.bg} rounded-3xl p-5 flex flex-col`} data-testid={`stat-${c.key}`}>
              <span className={`w-9 h-9 rounded-xl ${c.ring} flex items-center justify-center`}><Icon name={c.icon} className={`${c.ic} text-lg`} /></span>
              <p className="text-slate-600 text-sm font-medium mt-3">{c.label}</p>
              <p className={`text-2xl font-extrabold ${c.val} mt-0.5 tnum`}>{naira(c.value)}</p>
              <p className="text-xs text-slate-500 mt-0.5">{c.sub}</p>
              <button onClick={() => c.to === "wallet" ? onOpenWallet() : onNavigate(c.to)} data-testid={`stat-${c.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 text-slate-700 text-xs font-semibold hover:gap-2 transition-all">{c.cta}<Icon name="arrow_forward" className="text-sm" /></button>
            </div>
          )
        ))}
      </div>

      {/* Smart reminder */}
      {showReminder && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-100 rounded-3xl p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="smart-reminder">
          <span className="shrink-0 w-11 h-11 rounded-2xl bg-red-100 flex items-center justify-center"><Icon name="notifications_active" className="text-primary-custom text-2xl" /></span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black text-primary-custom tracking-widest">SMART REMINDER</p>
            <p className="font-bold text-slate-900 leading-tight">{smartReminder.headline}</p>
            <p className="text-sm text-slate-500">{smartReminder.message}</p>
          </div>
          <div className="flex items-center gap-2">
            {brandDot(smartReminder.provider)}
            <button onClick={() => onNavigate(smartReminder.service_type === "data" ? "data" : "airtime")} data-testid="reminder-topup" className="bg-primary-custom hover:bg-primary-dark text-white font-bold text-sm px-4 py-2.5 rounded-xl whitespace-nowrap">Top Up Now</button>
            <button onClick={() => setReminderDismissed(true)} data-testid="reminder-dismiss" className="p-1.5 text-slate-400 hover:text-slate-700"><Icon name="close" /></button>
          </div>
        </motion.div>
      )}

      {/* Row 3: Quick actions | Because you usually | Today's offers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-3xl p-5 border border-slate-200/70" data-testid="quick-actions">
          <h3 className="font-bold text-slate-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-3 gap-y-4 gap-x-2">
            {quickActions.map((a) => (
              <button key={a.label} onClick={a.onClick} data-testid={a.testid} className="flex flex-col items-center gap-1.5 group">
                <span className={`w-12 h-12 rounded-2xl ${a.bg} group-hover:scale-105 transition-transform flex items-center justify-center`}><Icon name={a.icon} className={`${a.ic} text-xl`} /></span>
                <span className="text-[11px] text-slate-600 font-medium text-center leading-tight">{a.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200/70" data-testid="because-you-usually">
          <h3 className="font-bold text-slate-900 mb-3">Because You Usually…</h3>
          {usual ? (
            <>
              <div className="flex items-center gap-3">
                {brandDot(usual.provider)}
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 text-sm truncate">Your usual {usual.provider} {usual.service_type === "data" ? "bundle" : "airtime"}</p>
                  <p className="text-xs text-slate-500">Bought {usual.count}× recently</p>
                </div>
                <p className="ml-auto font-extrabold text-slate-900">{naira(usual.amount)}</p>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => onNavigate(usual.service_type === "data" ? "data" : "airtime")} data-testid="buy-again" className="flex-1 bg-primary-custom hover:bg-primary-dark text-white text-sm font-bold py-2.5 rounded-xl">Buy Again</button>
                <button onClick={() => onNavigate(usual.service_type === "data" ? "data" : "airtime")} data-testid="see-other-plans" className="px-4 border border-primary-custom text-primary-custom text-sm font-semibold rounded-xl">See Other Plans</button>
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-400 py-6 text-center">Make a few top-ups and we'll suggest your usual here.</div>
          )}
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200/70" data-testid="todays-offers">
          <SectionHead title="Today's Offers" onViewAll={() => onNavigate("discover")} />
          <div className="space-y-3">
            {offers.slice(0, 4).map((o, i) => (
              <div key={o.id || i} className="flex items-center gap-3" data-testid={`offer-item-${i}`}>
                <span className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-base" style={{ background: (o.accent || "#E11D2E") + "1a" }}>{o.emoji || "🎁"}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-900 truncate">{o.brand} <span className="font-semibold" style={{ color: o.accent || "#E11D2E" }}>{o.title}</span></p>
                  <p className="text-xs text-slate-400 truncate">{o.subtitle}</p>
                </div>
                <button onClick={() => onNavigate("discover")} data-testid={`offer-claim-${i}`} className="text-xs font-bold text-primary-custom border border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-lg">Claim</button>
              </div>
            ))}
            {offers.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No offers right now.</p>}
          </div>
        </div>
      </div>

      {/* Row 4: Trending deal | Achievements | Need help */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-3xl p-5 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-100" data-testid="trending-deals">
          <SectionHead title="Trending Deals 🔥" onViewAll={() => onNavigate("data")} />
          {bestDeal ? (
            <div className="bg-white/70 rounded-2xl p-4">
              <div className="flex items-center gap-2">
                {brandDot(bestDeal.provider)}
                <span className="text-[10px] font-black bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full">{bestDeal.tag || "Best Value"}</span>
              </div>
              <p className="font-extrabold text-slate-900 mt-2 leading-tight">{bestDeal.description}</p>
              <div className="flex items-end gap-2 mt-1">
                <p className="text-xl font-extrabold text-slate-900">{naira(bestDeal.amount)}</p>
              </div>
              <button onClick={() => onNavigate("data")} data-testid="deal-buy" className="mt-3 w-full bg-primary-custom hover:bg-primary-dark text-white text-sm font-bold py-2.5 rounded-xl">Buy Now</button>
            </div>
          ) : <p className="text-sm text-slate-400 py-6 text-center">Loading deals…</p>}
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200/70" data-testid="achievements">
          <SectionHead title="Your Achievements" />
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: "local_fire_department", ic: "text-orange-500", bg: "bg-orange-50", top: `${summary?.streak?.current ?? 0} Day Streak`, sub: "Keep it up!" },
              { icon: "emoji_events", ic: "text-amber-500", bg: "bg-amber-50", top: `Saved ${naira(ach.total_saved || 0)}`, sub: "All time" },
              { icon: "card_giftcard", ic: "text-violet-500", bg: "bg-violet-50", top: `Earned ${naira(ach.total_earned || 0)}`, sub: "From referrals" },
              { icon: "workspace_premium", ic: "text-emerald-500", bg: "bg-emerald-50", top: `${tier.tier || "Bronze"} Referrer`, sub: tier.next_tier ? `${tier.to_next} to ${tier.next_tier}` : "Top tier" },
            ].map((a, i) => (
              <div key={i} className={`${a.bg} rounded-2xl p-3`} data-testid={`achievement-${i}`}>
                <Icon name={a.icon} className={`${a.ic} text-xl`} />
                <p className="font-bold text-slate-900 text-sm mt-1 leading-tight">{a.top}</p>
                <p className="text-[11px] text-slate-500">{a.sub}</p>
              </div>
            ))}
          </div>
          {summary?.spin?.available && (
            <button onClick={onOpenWheel} data-testid="home-spin" className="mt-3 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold py-2.5 rounded-xl flex items-center justify-center gap-1.5"><Icon name="casino" className="text-base" />Spin the wheel!</button>
          )}
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200/70" data-testid="need-help-card">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="headset_mic" className="text-primary-custom text-2xl" />
            <div>
              <h3 className="font-bold text-slate-900 leading-none">Need Help?</h3>
              <p className="text-xs text-slate-400 mt-1">We're here for you 24/7</p>
            </div>
          </div>
          <div className="space-y-2 mt-3">
            {[["chat", "Live Chat"], ["help", "Help Center"], ["report", "Report an Issue"]].map(([ic, l]) => (
              <button key={l} onClick={() => onNavigate("support")} className="w-full flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
                <Icon name={ic} className="text-primary-custom text-lg" />{l}</button>
            ))}
          </div>
          <button onClick={() => onNavigate("support")} data-testid="contact-support" className="mt-3 w-full bg-primary-custom hover:bg-primary-dark text-white rounded-xl text-sm font-bold py-2.5">Chat with us</button>
        </div>
      </div>

      {/* Row 5: Recent activity | Invite friends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-3xl p-5 border border-slate-200/70" data-testid="recent-tx-card">
          <SectionHead title="Recent Activity" onViewAll={() => onNavigate("transactions")} />
          {recent.length === 0 ? (
            <div className="text-center py-10 text-slate-400"><Icon name="receipt_long" className="text-5xl" /><p className="mt-2 text-sm">No transactions yet</p></div>
          ) : (
            <div className="divide-y divide-slate-100">{recent.map((tx) => <CompactTxRow key={tx.id} tx={tx} />)}</div>
          )}
        </div>

        <div className="relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-violet-100 to-fuchsia-100 border border-violet-200 flex items-center justify-between" data-testid="invite-friends-card">
          <div className="relative z-10 max-w-[65%]">
            <h3 className="font-extrabold text-violet-900 text-lg leading-tight">Invite Friends & Earn</h3>
            <p className="text-sm text-slate-600 mt-1">Earn up to ₦500 for every friend you invite.</p>
            <button onClick={() => onNavigate("referral")} data-testid="invite-now" className="mt-4 inline-flex items-center gap-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm px-5 py-2.5 rounded-xl">Invite Now<Icon name="arrow_forward" className="text-base" /></button>
          </div>
          <span className="text-6xl relative z-10">🎁</span>
          <div className="absolute -right-6 -bottom-8 w-40 h-40 bg-white/30 rounded-full blur-2xl" />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN DASHBOARD ORCHESTRATOR
// ============================================================

// Confirmation pop shown on the dashboard after returning from a successful recharge payment.
export const Dashboard = () => {
  const { user, logout } = useAuth();
  // Restore last visited page on refresh (persisted in localStorage)
  const VIEW_PAGE_IDS = ["dashboard", "airtime", "data", "cable_tv", "electricity", "transactions", "analytics", "referral", "family", "discover", "support"];
  const restoredPage = (() => {
    try { return localStorage.getItem("topit_active_page") || "dashboard"; } catch { return "dashboard"; }
  })();
  const [activeView, setActiveView] = useState(VIEW_PAGE_IDS.includes(restoredPage) ? restoredPage : "dashboard");
  const [showProfile, setShowProfile] = useState(restoredPage === "settings");
  const [showRefunds, setShowRefunds] = useState(false);
  const [showBeneficiaries, setShowBeneficiaries] = useState(restoredPage === "beneficiaries");
  const [showWallet, setShowWallet] = useState(restoredPage === "wallet");
  const [showSchedules, setShowSchedules] = useState(restoredPage === "schedules");
  const [showContacts, setShowContacts] = useState(restoredPage === "contacts");
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  const [txRange, setTxRange] = useState("all");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Persist current page so a browser refresh stays on the same page
  useEffect(() => {
    let page = activeView;
    if (showProfile) page = "settings";
    else if (showBeneficiaries) page = "beneficiaries";
    else if (showWallet) page = "wallet";
    else if (showSchedules) page = "schedules";
    else if (showContacts) page = "contacts";
    try { localStorage.setItem("topit_active_page", page); } catch { /* noop */ }
  }, [activeView, showProfile, showBeneficiaries, showWallet, showSchedules, showContacts]);

  const handleLogout = () => {
    try { localStorage.removeItem("topit_active_page"); } catch { /* noop */ }
    logout();
  };

  const [transactions, setTransactions] = useState([]);
  const [dataPlans, setDataPlans] = useState({});
  const [dataPlansLoading, setDataPlansLoading] = useState(true);
  const [dataPlansError, setDataPlansError] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(true);
  const [services, setServices] = useState({ cable_tv: false, electricity: false });
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [stats, setStats] = useState({ total_spent_month: 0, total_transactions_month: 0, total_savings_month: 0, active_beneficiaries: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const { summary: gamiSummary, refresh: refreshGami } = useGamification();
  const [wheelOpen, setWheelOpen] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [rechargeConfirm, setRechargeConfirm] = useState(null);

  const fetchWalletBalance = async () => {
    try {
      const res = await api.get("/wallet");
      setWalletBalance(res.data.total_balance || 0);
    } catch (e) { /* noop */ }
    finally { setWalletLoading(false); }
  };

  const fetchServices = async () => {
    try {
      const res = await api.get("/content");
      setServices({ cable_tv: !!res.data.cable_tv_enabled, electricity: !!res.data.electricity_enabled });
    } catch (e) { /* noop */ }
  };

  const fetchTransactions = async () => {
    try {
      const res = await api.get("/transactions/me");
      setTransactions(res.data || []);
    } catch (e) {
      // Fallback to public endpoint
      try {
        const res = await axios.get(`${API}/transactions`);
        setTransactions(res.data || []);
      } catch { /* noop */ }
    }
  };

  const fetchDataPlans = async () => {
    setDataPlansLoading(true); setDataPlansError(null);
    try {
      const res = await axios.get(`${API}/subscriptions`);
      if (res.data.success) {
        const plans = {};
        for (const [name, data] of Object.entries(res.data.providers)) {
          if (data.data && data.data.plans) plans[name] = data.data.plans;
        }
        setDataPlans(plans);
      } else {
        setDataPlansError("Failed to load data plans. Please try again.");
      }
    } catch (err) {
      setDataPlansError(err.response?.data?.detail || "Failed to connect to server. Please try again.");
    } finally {
      setDataPlansLoading(false);
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      const res = await api.get("/notifications?unread_only=true");
      setUnreadNotifications(res.data.unread_count);
    } catch (e) { /* noop */ }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const res = await api.get("/dashboard/stats");
      setStats(res.data);
    } catch (e) { /* noop */ }
    finally { setStatsLoading(false); }
  };

  useEffect(() => {
    fetchTransactions();
    fetchDataPlans();
    fetchWalletBalance();
    fetchServices();
    fetchUnreadNotifications();
    fetchStats();
    // Show a recharge confirmation returning from the payment callback (one-time)
    try {
      const raw = localStorage.getItem("topit_recharge_confirmation");
      if (raw) { setRechargeConfirm(JSON.parse(raw)); localStorage.removeItem("topit_recharge_confirmation"); }
    } catch { /* noop */ }
  }, []);

  const handleTransactionComplete = (usedWallet, skipCelebrate = false) => {
    fetchTransactions();
    fetchStats();
    fetchWalletBalance(); // always refresh — cashback can credit the wallet even on card payments
    // Dopamine loop: celebrate the purchase + refresh gamification after backend records it
    if (!skipCelebrate) setCelebrate(true);
    setTimeout(() => refreshGami(), 1200);
  };

  // Full-screen legacy overlays
  if (showRefunds) return <RefundPage onBack={() => { setShowRefunds(false); fetchWalletBalance(); }} />;
  if (showProfile) return <ProfilePage onBack={() => setShowProfile(false)} onOpenRefunds={() => { setShowProfile(false); setShowRefunds(true); }} onOpenNotifSettings={() => { setShowProfile(false); setShowNotifSettings(true); }} />;
  if (showNotifSettings) return <NotificationSettingsPage onBack={() => setShowNotifSettings(false)} />;
  if (showBeneficiaries) return <GroupsPage onBack={() => { setShowBeneficiaries(false); fetchStats(); }} dataPlans={dataPlans} />;
  if (showWallet) return <WalletPage onBack={() => { setShowWallet(false); fetchWalletBalance(); fetchStats(); }} />;
  if (showSchedules) return <ScheduledRechargesPage onBack={() => setShowSchedules(false)} dataPlans={dataPlans} />;
  if (showContacts) return <ContactsPage onBack={() => { setShowContacts(false); fetchTransactions(); }} walletBalance={walletBalance} dataPlans={dataPlans} onTransactionComplete={handleTransactionComplete} />;

  const hour = new Date().getHours();
  const dayPart = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  const firstName = user?.full_name?.split(" ")[0] || "there";
  const titleMap = {
    dashboard: `Good ${dayPart}, ${firstName}! 👋`,
    airtime: "Airtime Top-Up",
    data: "Data Bundles",
    cable_tv: "Cable TV",
    electricity: "Electricity",
    transactions: "Transactions",
    analytics: "Analytics",
    referral: "Refer & Earn",
    family: "Family Mode",
    discover: "Discover",
    support: "Support",
  };
  const subtitleMap = {
    dashboard: "Let's keep you connected and saving.",
    airtime: "Top up airtime instantly for any network.",
    data: "Browse and buy data bundles for all networks.",
    cable_tv: "Pay cable TV subscriptions in seconds.",
    electricity: "Settle electricity bills hassle-free.",
    transactions: "Your complete transaction history.",
    analytics: "Track your spending, networks, groups and rewards.",
    referral: "Invite friends and earn rewards on every top-up.",
    family: "Set monthly allowances — we auto top-up your family from your wallet.",
    discover: "Exclusive brand deals and perks for TopIt customers.",
    support: "Need a hand? We're here to help.",
  };

  return (
    <div className="min-h-screen bg-background-light">
      <Sidebar
        activeView={activeView}
        walletBalance={walletBalance}
        walletLoading={walletLoading}
        user={user}
        services={services}
        onNavigate={(v) => setActiveView(v)}
        onOpenWallet={() => setShowWallet(true)}
        onOpenBeneficiaries={() => setShowBeneficiaries(true)}
        onOpenContacts={() => setShowContacts(true)}
        onOpenSchedules={() => setShowSchedules(true)}
        onOpenSettings={() => setShowProfile(true)}
        onOpenNotifSettings={() => setShowNotifSettings(true)}
        onLogout={handleLogout}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      <div className="lg:ml-64 flex flex-col min-h-screen">
        <TopHeader
          title={titleMap[activeView] || "Dashboard"}
          subtitle={subtitleMap[activeView]}
          unreadNotifications={unreadNotifications}
          onOpenNotifications={() => setShowNotifications(true)}
          onOpenProfile={() => setShowProfile(true)}
          onLogout={handleLogout}
          onOpenMobileNav={() => setMobileNavOpen(true)}
          user={user}
        />

        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8 max-w-screen-2xl mx-auto w-full">
          {showNotifications && (
            <NotificationsDropdown onClose={() => { setShowNotifications(false); fetchUnreadNotifications(); }} />
          )}

          {activeView === "dashboard" && (
            <div className="2xl:flex 2xl:gap-6 2xl:items-start">
              <div className="2xl:flex-1 2xl:max-w-[1080px] w-full min-w-0">
                <DashboardHome
                  user={user}
                  walletBalance={walletBalance}
                  walletLoading={walletLoading}
                  transactions={transactions}
                  stats={stats}
                  statsLoading={statsLoading}
                  dataPlans={dataPlans}
                  dataPlansLoading={dataPlansLoading}
                  onOpenWallet={() => setShowWallet(true)}
                  onNavigate={(v) => setActiveView(v)}
                  onComplete={handleTransactionComplete}
                  summary={gamiSummary}
                  onOpenWheel={() => setWheelOpen(true)}
                  onOpenGroups={() => setShowBeneficiaries(true)}
                  onRefresh={() => { refreshGami(); fetchWalletBalance(); }}
                />
              </div>
              <AdvertPanel />
            </div>
          )}

          {activeView === "airtime" && (
            <VendForm
              initialServiceType="airtime"
              dataPlans={dataPlans}
              dataPlansLoading={dataPlansLoading}
              dataPlansError={dataPlansError}
              refetchDataPlans={fetchDataPlans}
              walletBalance={walletBalance}
              onTransactionComplete={handleTransactionComplete}
            />
          )}

          {activeView === "data" && (
            <VendForm
              initialServiceType="data"
              dataPlans={dataPlans}
              dataPlansLoading={dataPlansLoading}
              dataPlansError={dataPlansError}
              refetchDataPlans={fetchDataPlans}
              walletBalance={walletBalance}
              onTransactionComplete={handleTransactionComplete}
            />
          )}

          {activeView === "cable_tv" && <ComingSoonView label="Cable TV" icon="live_tv" />}
          {activeView === "electricity" && <ComingSoonView label="Electricity" icon="bolt" />}

          {activeView === "transactions" && (
            <div className="space-y-4 max-w-4xl" data-testid="transactions-view">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-slate-900 text-lg">All Transactions</h2>
                <button onClick={fetchTransactions} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="refresh-transactions">
                  <Icon name="refresh" className="text-slate-600" />
                </button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="tx-date-filters">
                {[
                  { id: "all", label: "All" },
                  { id: "today", label: "Today" },
                  { id: "7d", label: "Past 7 Days" },
                  { id: "30d", label: "Past 30 Days" },
                  { id: "3m", label: "Past 3 Months" },
                  { id: "year", label: "This Year" },
                  { id: "lastyear", label: "Last Year" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setTxRange(f.id)}
                    data-testid={`tx-filter-${f.id}`}
                    className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${txRange === f.id ? "bg-primary-custom text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {(() => {
                const inRange = (tx) => {
                  if (txRange === "all") return true;
                  if (!tx.timestamp) return false;
                  const d = new Date(tx.timestamp);
                  const now = new Date();
                  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  if (txRange === "today") return d >= startOfToday;
                  if (txRange === "7d") return d >= new Date(startOfToday.getTime() - 6 * 864e5);
                  if (txRange === "30d") return d >= new Date(startOfToday.getTime() - 29 * 864e5);
                  if (txRange === "3m") { const s = new Date(startOfToday); s.setMonth(s.getMonth() - 3); return d >= s; }
                  if (txRange === "year") return d >= new Date(now.getFullYear(), 0, 1);
                  if (txRange === "lastyear") return d >= new Date(now.getFullYear() - 1, 0, 1) && d < new Date(now.getFullYear(), 0, 1);
                  return true;
                };
                const list = transactions.filter(inRange);
                if (transactions.length === 0) {
                  return (
                    <div className="text-center py-16 text-slate-400 bg-white rounded-3xl border border-slate-200/70">
                      <Icon name="receipt_long" className="text-5xl" />
                      <p className="mt-2">No transactions yet</p>
                    </div>
                  );
                }
                if (list.length === 0) {
                  return (
                    <div className="text-center py-16 text-slate-400 bg-white rounded-3xl border border-slate-200/70" data-testid="tx-empty-range">
                      <Icon name="event_busy" className="text-5xl" />
                      <p className="mt-2">No transactions in this period</p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-3">
                    {list.map((tx) => <TransactionItem key={tx.id} transaction={tx} />)}
                  </div>
                );
              })()}
            </div>
          )}

          {activeView === "support" && <SupportView />}

          {activeView === "analytics" && (
            <AnalyticsPage
              onNavigate={(v) => setActiveView(v)}
              onOpenBeneficiaries={() => setShowBeneficiaries(true)}
              onOpenWallet={() => setShowWallet(true)}
            />
          )}

          {activeView === "referral" && (
            <ReferralProgram
              onNavigate={(v) => setActiveView(v)}
              onOpenWallet={() => setShowWallet(true)}
            />
          )}

          {activeView === "family" && (
            <FamilyPage dataPlans={dataPlans} onWalletChanged={() => { fetchWalletBalance(); }} />
          )}

          {activeView === "discover" && <DiscoverPage />}
        </main>
      </div>

      <PostPurchaseCelebration
        open={celebrate}
        onClose={() => { setCelebrate(false); refreshGami(); }}
        onSpin={() => setWheelOpen(true)}
      />
      <RewardsWheelModal
        open={wheelOpen}
        wheel={gamiSummary?.wheel}
        onClose={() => { setWheelOpen(false); refreshGami(); fetchWalletBalance(); }}
        onSpun={() => { refreshGami(); fetchWalletBalance(); }}
      />
      <AIAssistant
        user={user}
        onOpenWallet={() => setShowWallet(true)}
        onPurchase={(recharge) => {
          // Refresh balances/txns/stats without the separate confetti overlay;
          // the branded receipt (with its own confetti) is the sole celebration.
          handleTransactionComplete(true, true);
          if (recharge) setRechargeConfirm(recharge);
        }}
      />
      <RechargeConfirmationModal data={rechargeConfirm} onClose={() => setRechargeConfirm(null)} />
      <Toaster richColors position="top-center" />
    </div>
  );
};



// Auth Provider Component
